import angular from 'angular';

import config from '../../config';

export default class NotificationCollection {
  constructor(Restangular, StatusService, $rootScope, $q, $timeout) {
    'ngInject';

    Object.assign(this, { Restangular, StatusService, $rootScope, $q, $timeout });
    this.init();
  }

  init() {
    this.pageSize = 20;
    this._page = null;
    this._archived = null;
    this.clearData();

    // Real time socket updates
    this.$rootScope.$on('socket:notification:new', (event, data) => {
      console.log('notification:new');
      this.addOrEditNotification(data);
    });

    this.$rootScope.$on('socket:notification:edit', (event, data) => {
      console.log('notification:edit');
      this.addOrEditNotification(data);
    });

    this.$rootScope.$on('socket:notification:delete', (event, data) => {
      this.deleteNotification(data);
    });
  }

  _getNotifications(archived) {
    return archived ? this.archivedNotifications : this.notifications;
  }

  _filterNotifications() {
    this.notifications = [];
    this.archivedNotifications = [];

    if (this.allNotifications) {
      this.allNotifications.forEach((notification) => {
        if (notification.archived) {
          this.archivedNotifications.push(notification);
        } else {
          this.notifications.push(notification);
        }
      });
    }

    const notifications = this._getNotifications(this._archived);
    const minimum = this._page * this.pageSize;
    const maximum = (this._page + 1) * this.pageSize;
    this.myPage = notifications.slice(minimum, maximum);
  }

  clearData() {
    this.allNotifications = null;
    this.notifications = [];
    this.archivedNotifications = [];
    this.myPage = [];
  }

  async _loadFromServer(archived, page = 0) {
    const rawData = await this.Restangular.all('notifications').getList({
      page,
      archived,
      pagesize: this.pageSize,
    });
    const data = rawData.filter(Boolean); // undefined in rawData means a malformed notification

    this.allNotifications = this.allNotifications || [];

    if (!data.length && page > 0) {
      // This page has nothing, go back one page
      // In the future we'll get a totalItems from the server preventing this kind of mess
      this._page--;
    }

    data.forEach((notification) => {
      const existing = this.allNotifications.find((n) => n.id === notification.id);
      if (!existing) {
        this.allNotifications.push(notification);
      }
    });

    this._filterNotifications();

    return true;
  }

  setDirty() {
    this.allNotifications = null;
  }

  getNotifications(archived, page) {
    // We need to make sure that you're requesting a valid page. For example, if you're requesting
    // page 0 then there can't already be more then 20 items in the local array. Or if you're requesting page 2
    // but there are only 20 items in the local array, then you should really request page 1 first. This way
    // we prevent gaps in the array

    // Prevent refetching the same page over and over when that page wasn't fully filled.
    // For example you're on page 0 of the un-archived notifications. There are only 10 of them.
    // When switching away from the Dashboard and back again, you don't want to load this page from the server.
    // If we don't add this check here, the minimum / maximum logic down below would cause another request.

    if (!this.allNotifications) {
      /**
       * allNotifications might be cleared out due to having dirty data in collection (socket error)
       * we need to make sure we clean it and fetch stuff again
       */
      this.clearData();
    }

    if (this.allNotifications && this._archived === archived && this._page === page) {
      this._filterNotifications();
      return this.$q.resolve();
    }

    this._archived = archived;
    this._page = page;

    const minimum = page * this.pageSize;
    const maximum = (page + 1) * this.pageSize;
    const notifications = this._getNotifications(archived);

    // You're requesting a page that's already loaded. Bail out with a resolve.
    if (notifications.length >= maximum) {
      if (!__TEST__) {
        console.log(
          `we already have ${notifications.length} notifications! No need to request page ${page}`
        );
      }
      this._filterNotifications();
      return this.$q.resolve();
    }

    if (notifications.length >= minimum && notifications.length < maximum) {
      if (!__TEST__) {
        console.log(`Requesting page ${page}, archived: ${archived}`);
      }
      return this._loadFromServer(archived, page);
    }

    return this.$q.reject();
  }

  refresh() {
    if (!__TEST__) {
      console.log('refresh NotificationCollection');
    }

    if (this.allNotifications) {
      this.clearData();
      return this.getNotifications(this._archived, this._page);
    }

    return this.$q.resolve();
  }

  async readAll() {
    if (config.SKIP_MARK_AS_READ) {
      return;
    }

    await this.$timeout();
    this.Restangular.all('notifications/read').customPUT();
    this.StatusService.unreadNotifications = 0;

    if (this.allNotifications) {
      this.allNotifications.forEach((notification) => {
        notification.read = true;
      });
    }
  }

  addOrEditNotification(notification) {
    if (!__TEST__) {
      console.log('addOrEditNotification', notification);
    }

    if (this.allNotifications) {
      const existingNotification = this.allNotifications.find((n) => n.id === notification.id);

      if (existingNotification) {
        // Update
        angular.extend(existingNotification, notification);
        this.Restangular.restangularizeElement(null, existingNotification, 'notifications');
      } else {
        // Insert
        this.Restangular.restangularizeElement(null, notification, 'notifications');
        this.allNotifications.unshift(notification);
      }

      this._filterNotifications();
    }

    return notification;
  }

  deleteNotification(notification) {
    if (this.allNotifications) {
      this.allNotifications = this.allNotifications.filter((n) => n.id !== notification.id);
      this._filterNotifications();
    }
  }

  async retrieveNotification(id, httpConfig = {}) {
    const notification = await this.Restangular.all('notifications')
      .one(id)
      .withHttpConfig(httpConfig)
      .get();
    return this.Restangular.restangularizeElement(null, notification, 'notifications');
  }
}
